import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";

// Country scope state — persisted in localStorage so it sticks between sessions.
// Values: "ALL" | "GH" | "TG" | "BJ"
const STORAGE_KEY = "portside_country_scope";

const COUNTRIES = {
  GH: { code: "GH", name: "Ghana",  flag: "🇬🇭", currency: "GHS", fuelStation: true,  lang: "en" },
  TG: { code: "TG", name: "Togo",   flag: "🇹🇬", currency: "XOF", fuelStation: false, lang: "fr" },
  BJ: { code: "BJ", name: "Benin",  flag: "🇧🇯", currency: "XOF", fuelStation: false, lang: "fr" },
};

const CountryCtx = createContext(null);

export function CountryProvider({ children }) {
  const { user } = useAuth();
  // user.allowed_countries — null/undefined/empty = unrestricted (admins are always unrestricted)
  const userAllowed = useMemo(() => {
    if (!user) return null;
    if (user.role === "admin") return null;
    const ac = user.allowed_countries;
    if (!Array.isArray(ac) || ac.length === 0) return null;
    return ac.filter((c) => c === "GH" || c === "TG" || c === "BJ");
  }, [user]);

  // The list of country codes the switcher should expose ("ALL" allowed iff unrestricted OR multi-country)
  const allowedCountries = userAllowed ?? ["GH", "TG", "BJ"];
  const canSeeAll = userAllowed === null || userAllowed.length > 1;

  const [scope, setScopeState] = useState(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      return v === "GH" || v === "TG" || v === "BJ" || v === "ALL" ? v : "ALL";
    } catch {
      return "ALL";
    }
  });

  // If the stored scope isn't allowed for this user, snap it back into the allowed set.
  useEffect(() => {
    if (!user) return;
    const ok = scope === "ALL" ? canSeeAll : allowedCountries.includes(scope);
    if (ok) return;
    const next = userAllowed && userAllowed.length === 1 ? userAllowed[0] : "ALL";
    setScopeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
  }, [user, userAllowed, allowedCountries, canSeeAll, scope]);

  const setScope = (v) => {
    // Refuse to switch into a country this user can't see.
    if (v === "ALL" && !canSeeAll) return;
    if (v !== "ALL" && !allowedCountries.includes(v)) return;
    setScopeState(v);
    try { localStorage.setItem(STORAGE_KEY, v); } catch { /* ignore */ }
  };

  // Helpers
  const isAll = scope === "ALL";
  const isTG  = scope === "TG";
  const isBJ  = scope === "BJ";
  const isGH  = scope === "GH";

  // When "ALL", treat as Ghana for new-record defaults (but only if user can see GH)
  const defaultCountry = isTG ? "TG" : (isBJ ? "BJ" : (allowedCountries.includes("GH") ? "GH" : allowedCountries[0]));

  // Filter param to pass to backend list endpoints
  const filterParam = useMemo(() => (isAll ? undefined : scope), [scope, isAll]);

  // Show fuel-related UI only in GH or ALL (TG & BJ have no yard fuel station)
  const showFuel = !isTG && !isBJ;

  const value = {
    scope,
    setScope,
    isAll, isTG, isBJ, isGH,
    defaultCountry,
    filterParam,
    showFuel,
    COUNTRIES,
    allowedCountries,   // codes this user is allowed to see (or all 3 if unrestricted)
    canSeeAll,          // whether the "ALL" option should appear in the switcher
  };

  return <CountryCtx.Provider value={value}>{children}</CountryCtx.Provider>;
}

export const useCountry = () => {
  const ctx = useContext(CountryCtx);
  if (!ctx) throw new Error("useCountry must be used within CountryProvider");
  return ctx;
};

export const countryInfo = (code) => COUNTRIES[code] || COUNTRIES.GH;
